import { DateTime } from "luxon";
import { rrulestr } from "rrule";

import Filters from "../../../../filters";
import { generateFrequencyString } from "../../../../Recurrence";
import { buildNachaPassthroughDetails } from "../nachaPassthrough/helpers";

import type {
  AnyDualApprovalState,
  PaginatedBulkDetails,
  PaginatedDualApproval,
  PaginatedFxQuote,
  PaginatedPaymentDetails,
  PaginatedRecipientDetails,
  PaginatedSchedule,
  PaginatedTemplateVersionDetails,
  PaginatedWireImportDetails,
  WireImportBatchDualApprovalState,
} from "./types";
import type { PaginatedNachaPassthroughDetails } from "../nachaPassthrough/types";

type RecentlyChangedDualApprovalParams = {
  createdAt?: string | null;
  recipientUpdatedAt?: string | null;
  achId?: string | null;
  fedNowTransactionId?: string | null;
  scheduledACHId?: string | null;
  now?: DateTime;
};

// Utility Functions
export const shouldShowRecentlyChanged = ({
  createdAt,
  recipientUpdatedAt,
  achId,
  fedNowTransactionId,
  scheduledACHId,
  now = DateTime.utc(),
}: RecentlyChangedDualApprovalParams): boolean => {
  if (
    (!achId && !fedNowTransactionId && !scheduledACHId) ||
    !createdAt ||
    !recipientUpdatedAt
  ) {
    return false;
  }

  const paymentCreatedDate = DateTime.fromISO(createdAt).toUTC();
  const recipientUpdateDate = DateTime.fromISO(recipientUpdatedAt).toUTC();

  if (!paymentCreatedDate.isValid || !recipientUpdateDate.isValid) {
    return false;
  }

  const normalizedNow = now.toUTC();
  const threeDaysBeforePayment = paymentCreatedDate.minus({ days: 3 });
  const threeDaysAfterPayment = paymentCreatedDate.plus({ days: 3 });
  const threeDaysAfterRecipientUpdate = recipientUpdateDate.plus({ days: 3 });

  return (
    (recipientUpdateDate >= threeDaysBeforePayment &&
      normalizedNow <= threeDaysAfterPayment) ||
    normalizedNow <= threeDaysAfterRecipientUpdate
  );
};

const getScheduledDate = (recurringRule: string): string | undefined => {
  if (!recurringRule) return undefined;
  const rrule = rrulestr(recurringRule);
  return rrule.options.dtstart?.toISOString();
};

const getRecurrence = (recurringRule: string): number | null => {
  if (!recurringRule) return null;
  const rrule = rrulestr(recurringRule);
  if (rrule.options.count) return rrule.options.count;
  if (rrule.options.until) return rrule.count();
  return null;
};

const getScheduleEndDate = (recurringRule: string): string | undefined => {
  if (!recurringRule) return undefined;
  const rrule = rrulestr(recurringRule);
  const { until } = rrule.options;
  if (until) {
    const allOccurrences = rrule.all();
    if (allOccurrences.length > 0) {
      return allOccurrences[allOccurrences.length - 1].toISOString();
    }
  }
  return undefined;
};

const getFrequency = (recurringRule: string): string | undefined => {
  if (!recurringRule) return undefined;
  const recurrence = getRecurrence(recurringRule);
  if ((recurrence && recurrence > 1) || !recurrence) {
    const rrule = rrulestr(recurringRule);
    return generateFrequencyString(rrule);
  }
  return "";
};

const getNetTotalDollarsWithoutSign = (
  debitAmount: number,
  creditAmount: number,
): string => {
  const netTotal = debitAmount - creditAmount;
  return Filters.currency(
    Filters.penniesToDollars(Math.abs(netTotal)) as Dollars,
    { hasDecimal: true },
  );
};

const formatPenniesAsDollars = (pennies: number): string =>
  Filters.currency(Filters.penniesToDollars(pennies) as Dollars, {
    hasDecimal: true,
  });

const getBulkAmountFields = (
  debitAmount?: number,
  creditAmount?: number,
): Pick<
  PaginatedBulkDetails,
  "netTotalDollarsWithoutSign" | "creditTotalDollars" | "debitTotalDollars"
> => ({
  ...(debitAmount !== undefined &&
    creditAmount !== undefined && {
      netTotalDollarsWithoutSign: getNetTotalDollarsWithoutSign(
        debitAmount,
        creditAmount,
      ),
    }),
  ...(creditAmount !== undefined && {
    creditTotalDollars: formatPenniesAsDollars(creditAmount),
  }),
  ...(debitAmount !== undefined && {
    debitTotalDollars: formatPenniesAsDollars(debitAmount),
  }),
});

const getDateRange = (
  dates: (string | null)[],
): { short: string; long: string; noYear: string } => {
  if (!dates || dates.length === 0) return { short: "", long: "", noYear: "" };

  const parsedDates = dates
    .map((d) => (d ? DateTime.fromISO(d) : null))
    .filter((d): d is DateTime => d !== null);
  const hasNull = dates.includes(null);

  if (parsedDates.length === 0) {
    return {
      short: "Upon approval",
      long: "Upon approval",
      noYear: "Upon approval",
    };
  }

  const firstDate = parsedDates[0];
  const lastDate = parsedDates[parsedDates.length - 1];
  const formatShort = (dt: DateTime) => dt.toFormat("MMM d, yyyy");
  const formatLong = (dt: DateTime) => dt.toFormat("MMMM d, yyyy");
  const formatNoYear = (dt: DateTime) => dt.toFormat("MMMM d");
  const allSameDate = parsedDates.every((d) => d.hasSame(firstDate, "day"));

  if (hasNull) {
    const displayShort = allSameDate
      ? formatShort(firstDate)
      : formatShort(lastDate);
    const displayLong = allSameDate
      ? formatLong(firstDate)
      : formatLong(lastDate);
    const displayNoYear = allSameDate
      ? formatNoYear(firstDate)
      : formatNoYear(lastDate);
    return {
      short: `Upon approval - ${displayShort}`,
      long: `Upon approval - ${displayLong}`,
      noYear: `Upon approval - ${displayNoYear}`,
    };
  }

  if (allSameDate) {
    return {
      short: formatShort(firstDate),
      long: formatLong(firstDate),
      noYear: formatNoYear(firstDate),
    };
  }

  return {
    short: `${firstDate.toFormat("MMM d")} - ${formatShort(lastDate)}`,
    long: `${formatLong(firstDate)} - ${formatLong(lastDate)}`,
    noYear: `${formatNoYear(firstDate)} - ${formatNoYear(lastDate)}`,
  };
};

const getRecipientName = (
  dualApproval: AnyDualApprovalState,
  matchedRecipient?: API.Recipient,
): string => {
  if ("nachaPassthroughSubmission" in dualApproval) {
    const count =
      dualApproval.paymentCreditCount + dualApproval.paymentDebitCount;
    return `${count} ${count === 1 ? "recipient" : "recipients"}`;
  }
  if ("recipientName" in dualApproval && dualApproval.recipientName) {
    return dualApproval.recipientName;
  }
  if (matchedRecipient) {
    return matchedRecipient.name || "";
  }
  if ("recipientCount" in dualApproval) {
    const count = dualApproval.recipientCount || 0;
    return `${count} ${count > 1 ? "recipients" : "recipient"}`;
  }
  if (
    "templateName" in dualApproval &&
    "bulkPaymentTemplateVersion" in dualApproval
  ) {
    return dualApproval.templateName || "";
  }
  return "";
};

const getRecipientDetails = (
  dualApproval: AnyDualApprovalState,
  matchedRecipient?: API.Recipient,
): PaginatedRecipientDetails | undefined => {
  if ("wire" in dualApproval) {
    return {
      bankName: dualApproval.wireBankName,
      accountNumber: dualApproval.wireAccountNumber,
      routingNumber: dualApproval.wireRoutingNumber,
      address: dualApproval.wireAddress,
    };
  }

  if (
    "international_wire" in dualApproval &&
    matchedRecipient?.swift_destination
  ) {
    const { swift_destination: dest } = matchedRecipient;
    return {
      lastUpdatedAt: matchedRecipient.updated_at,
      bic: dest.bic,
      accountIdentifier: dest.account_identifier,
      addressObj: dest.address || undefined,
      isOrphanedDestination: false,
      correspondentBankBic: dest.correspondent_bank_bic,
      bankName: dest.metadata?.bank_name,
      branchAddress: dest.branch_address,
    };
  }

  if ("international_wire" in dualApproval && !matchedRecipient) {
    return {
      isOrphanedDestination: true,
    };
  }

  if (matchedRecipient?.ach_destination) {
    return {
      lastUpdatedAt: matchedRecipient.updated_at,
      bankName: matchedRecipient.ach_destination.institution_name,
      accountNumber: matchedRecipient.ach_destination.account_number,
      routingNumber: matchedRecipient.ach_destination.routing_number,
    };
  }

  return undefined;
};

const getPaymentDetails = (
  dualApproval: AnyDualApprovalState,
): PaginatedPaymentDetails | undefined => {
  if ("wire" in dualApproval) {
    return {
      memo: dualApproval.wireMemo,
      wireReason: dualApproval.wireReason,
      templateName: dualApproval.templateName,
    };
  }

  if ("international_wire" in dualApproval) {
    return {
      wireReason: dualApproval.wireReason,
      memo: dualApproval.memo,
      note: dualApproval.note,
      templateName: dualApproval.templateName,
    };
  }

  if ("bulkPayment" in dualApproval && !("bulkReversal" in dualApproval)) {
    return {
      entryDescription: dualApproval.entryDescription,
      companyName: dualApproval.companyName,
      companyId: dualApproval.companyId,
      description: dualApproval.description,
      secCode: dualApproval.secCode,
    };
  }

  // ACH, ScheduledACH, or ACH Reversal
  return {
    memo: "memo" in dualApproval ? dualApproval.memo : undefined,
    note: "note" in dualApproval ? (dualApproval.note ?? undefined) : undefined,
    paymentIdentification:
      "paymentIdentification" in dualApproval
        ? dualApproval.paymentIdentification
        : undefined,
    entryDescription:
      "entryDescription" in dualApproval
        ? dualApproval.entryDescription
        : undefined,
    companyName:
      "companyName" in dualApproval ? dualApproval.companyName : undefined,
    companyId: "companyId" in dualApproval ? dualApproval.companyId : undefined,
    secCode:
      "secCode" in dualApproval
        ? (dualApproval.secCode ?? undefined)
        : undefined,
    reversalReason:
      "reversalReason" in dualApproval
        ? (dualApproval.reversalReason ?? undefined)
        : undefined,
  };
};

const getSchedulingFields = (
  dualApproval: AnyDualApprovalState,
): PaginatedSchedule | undefined => {
  if (!("recurringRule" in dualApproval) || !dualApproval.recurringRule) {
    return undefined;
  }
  const { recurringRule } = dualApproval;
  const scheduledDate = getScheduledDate(recurringRule);
  const frequency = getFrequency(recurringRule);
  const recurrence = getRecurrence(recurringRule);

  return {
    ...(scheduledDate && { scheduledDate }),
    ...(frequency !== undefined && { frequency }),
    ...(recurrence !== undefined && { recurrence }),
    endDate: getScheduleEndDate(recurringRule),
  };
};

const getBulkReversalFields = (
  dualApproval: AnyDualApprovalState,
): PaginatedBulkDetails | undefined => {
  if (!("bulkReversal" in dualApproval)) {
    return undefined;
  }

  const {
    debitAmount,
    creditAmount,
    paymentCount,
    paymentCreditCount,
    paymentDebitCount,
  } = dualApproval;

  return {
    paymentCount,
    paymentCreditCount,
    paymentDebitCount,
    ...(dualApproval.netTotalDollarsWithSign && {
      netTotalDollarsWithSign: dualApproval.netTotalDollarsWithSign,
    }),
    ...getBulkAmountFields(debitAmount, creditAmount),
  };
};

const getBulkFields = (
  dualApproval: AnyDualApprovalState,
): PaginatedBulkDetails | undefined => {
  if (!("bulkPayment" in dualApproval) || "bulkReversal" in dualApproval) {
    return undefined;
  }

  const { debitAmount, creditAmount, scheduledDates } = dualApproval;
  const dateRangeResult = scheduledDates
    ? getDateRange(scheduledDates)
    : undefined;

  return {
    recipientCount: dualApproval.recipientCount,
    paymentCount: dualApproval.paymentCount,
    title: dualApproval.paymentTitle,
    isExpedited: dualApproval.isExpedited,
    groupPosting: dualApproval.groupPosting,
    paymentCreditCount: dualApproval.paymentCreditCount,
    paymentDebitCount: dualApproval.paymentDebitCount,
    ...(dualApproval.netTotalDollarsWithSign && {
      netTotalDollarsWithSign: dualApproval.netTotalDollarsWithSign,
    }),
    ...(dateRangeResult && {
      dateRange: dateRangeResult.short,
      longDateRange: dateRangeResult.long,
      noYearDateRange: dateRangeResult.noYear,
    }),
    ...getBulkAmountFields(debitAmount, creditAmount),
  };
};

const getWireImportFields = (
  dualApproval: AnyDualApprovalState,
): PaginatedWireImportDetails | undefined => {
  if (!("wireImportBatch" in dualApproval)) return undefined;

  const wireImportDA = dualApproval as WireImportBatchDualApprovalState;
  return {
    description: wireImportDA.description,
    wireCount: wireImportDA.wireCount,
    recipientCount: wireImportDA.recipientCount,
    fileName: wireImportDA.fileName,
    note: wireImportDA.note,
  };
};

const getNachaPassthroughFields = (
  dualApproval: AnyDualApprovalState,
): PaginatedNachaPassthroughDetails | undefined =>
  "nachaPassthroughSubmission" in dualApproval
    ? buildNachaPassthroughDetails(dualApproval)
    : undefined;

const getFxQuote = (
  dualApproval: AnyDualApprovalState,
): PaginatedFxQuote | undefined => {
  if (!("international_wire" in dualApproval) || !("quote" in dualApproval)) {
    return undefined;
  }
  const { quote } = dualApproval;
  if (!quote) return undefined;

  return {
    specifiedAmount: quote.amount,
    specifiedCurrency: quote.specified_currency,
    convertedAmount: quote.converted_amount,
    convertedCurrency: quote.converted_currency,
    settlementCurrency: quote.settlement_currency,
    rate: quote.rate,
  };
};

const getTemplateVersionFields = (
  dualApproval: AnyDualApprovalState,
): PaginatedTemplateVersionDetails | undefined => {
  if (!("bulkPaymentTemplateVersion" in dualApproval)) return undefined;

  const {
    templateName,
    versionNumber,
    templateGroupHasActiveVersion,
    institutionAccountId,
    institutionAccountName,
    institutionAccountLastFour,
    secCode,
    entryDesc,
    description,
    maxExpediteChoice,
    groupPosting,
    userChangeLog,
    userFullName,
    paymentData,
    paymentCount,
    paymentCreditCount,
    paymentDebitCount,
    creditAmount,
    debitAmount,
  } = dualApproval;

  const creditTotalDollars =
    paymentCreditCount > 0
      ? Filters.currency(creditAmount as Cents, { hasDecimal: false })
      : null;
  const debitTotalDollars =
    paymentDebitCount > 0
      ? Filters.currency(debitAmount as Cents, { hasDecimal: false })
      : null;
  const netAmountCents = (creditAmount ?? 0) - (debitAmount ?? 0);
  const netTotalDollars = Filters.currency(Math.abs(netAmountCents) as Cents, {
    hasDecimal: false,
  });

  return {
    templateName,
    versionNumber,
    templateGroupHasActiveVersion,
    institutionAccountId,
    institutionAccountName,
    institutionAccountLastFour,
    secCode,
    entryDesc,
    description,
    maxExpediteChoice,
    groupPosting,
    userChangeLog,
    userFullName,
    paymentData,
    paymentCount,
    paymentCreditCount,
    paymentDebitCount,
    creditTotalDollars,
    debitTotalDollars,
    netTotalDollars,
  };
};

export const formatDualApproval = (
  dualApproval: AnyDualApprovalState,
  recipients: API.Recipient[],
  currentUserId?: API.UserId,
): PaginatedDualApproval => {
  const recipientId =
    ("recipientId" in dualApproval && dualApproval.recipientId) ||
    ("recipient" in dualApproval &&
      (dualApproval.recipient as API.RecipientId | undefined)) ||
    undefined;
  const matchedRecipient = recipientId
    ? recipients.find((r) => r.id === recipientId)
    : undefined;

  const schedule = getSchedulingFields(dualApproval);
  const bulk =
    getBulkFields(dualApproval) ?? getBulkReversalFields(dualApproval);
  const wireImport = getWireImportFields(dualApproval);
  const nachaPassthrough = getNachaPassthroughFields(dualApproval);
  const templateVersion = getTemplateVersionFields(dualApproval);
  const recipient = getRecipientDetails(dualApproval, matchedRecipient);
  const payment = getPaymentDetails(dualApproval);
  const fxQuote = getFxQuote(dualApproval);

  return {
    // core fields
    id: dualApproval.id,
    displayType: dualApproval.displayType,
    dollarAmount: dualApproval.dollarAmount,
    recipientName: getRecipientName(dualApproval, matchedRecipient),
    requesterName: dualApproval.requester?.name ?? "",
    isOwnRequest: currentUserId === dualApproval.requester?.id,
    createdAt: dualApproval.created_at,
    ...(dualApproval.expired_at && { expiredAt: dualApproval.expired_at }),
    status: dualApproval.status,
    ...(dualApproval.rejectionReason && {
      rejectionReason: dualApproval.rejectionReason,
    }),
    // type discriminators
    ...("wire" in dualApproval && { wireId: dualApproval.wire }),
    ...("international_wire" in dualApproval && {
      internationalWireId: dualApproval.international_wire,
    }),
    ...("ach" in dualApproval && { achId: dualApproval.ach }),
    ...("fedNowTransaction" in dualApproval && {
      fedNowTransactionId: dualApproval.fedNowTransaction,
    }),
    ...("achReversal" in dualApproval && {
      achReversalId: dualApproval.achReversal,
    }),
    ...("fedNowTransaction" in dualApproval && {
      fedNowTransactionId: dualApproval.fedNowTransaction,
    }),
    ...("scheduledACH" in dualApproval && {
      scheduledACHId: dualApproval.scheduledACH,
    }),
    ...("bulkPayment" in dualApproval &&
      !("bulkReversal" in dualApproval) && {
        bulkPaymentId: dualApproval.bulkPayment,
      }),
    ...("bulkReversal" in dualApproval && {
      bulkReversalId: dualApproval.bulkReversal,
      bulkReversalParentPaymentId: dualApproval.bulkPayment,
    }),
    ...("bulkPaymentTemplateVersion" in dualApproval && {
      bulkPaymentTemplateVersionId: dualApproval.bulkPaymentTemplateVersion,
    }),
    ...("wireImportBatch" in dualApproval && {
      wireImportBatchId: dualApproval.wireImportBatch,
    }),
    ...("nachaPassthroughSubmission" in dualApproval && {
      nachaPassthroughSubmissionId: dualApproval.nachaPassthroughSubmission,
    }),
    // account fields
    ...(dualApproval.fromAccountId && {
      fromAccountId: dualApproval.fromAccountId,
    }),
    ...(dualApproval.fromAccountDisplay && {
      fromAccountDisplay: dualApproval.fromAccountDisplay,
    }),
    ...(dualApproval.transactionType && {
      transactionType: dualApproval.transactionType,
    }),
    ...(dualApproval.isSameDay && { isSameDay: true }),
    // nested details
    ...(schedule && { schedule }),
    ...(bulk && { bulk }),
    ...(wireImport && { wireImport }),
    ...(nachaPassthrough && { nachaPassthrough }),
    ...(templateVersion && { templateVersion }),
    ...(recipient && { recipient }),
    ...(payment && { payment }),
    ...(fxQuote && { fxQuote }),
  };
};
